// src/components/modals/AssignInventoryModal.jsx
import { useState, useEffect } from 'react'
import { X, Loader2, Check, Package, ChevronDown } from 'lucide-react'
import { Button } from '../ui/Button'
import RawMaterialInventoryService from '../../services/rawMaterialInventoryService'

const AssignInventoryModal = ({ isOpen, onClose, onInventoryAssigned, branches = [] }) => {
  const [formData, setFormData] = useState({
    branch: '',
    selectedMaterials: []
  })
  const [unassignedMaterials, setUnassignedMaterials] = useState([])
  const [loading, setLoading] = useState(false)
  const [materialsLoading, setMaterialsLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [selectAll, setSelectAll] = useState(false)

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        branch: '',
        selectedMaterials: []
      })
      setUnassignedMaterials([])
      setErrors({})
      setSelectAll(false)
    }
  }, [isOpen])

  // Fetch unassigned materials when branch is selected
  useEffect(() => {
    if (formData.branch) {
      fetchUnassignedMaterials(formData.branch)
    } else {
      setUnassignedMaterials([])
      setFormData(prev => ({ ...prev, selectedMaterials: [] }))
    }
  }, [formData.branch])

  // Handle branch selection
  const handleBranchChange = (e) => {
    const branchId = e.target.value
    setFormData(prev => ({
      ...prev,
      branch: branchId,
      selectedMaterials: []
    }))
    setSelectAll(false)

    // Clear error when user selects branch
    if (errors.branch) {
      setErrors(prev => ({
        ...prev,
        branch: ''
      }))
    }
  }

  // Fetch unassigned raw materials for selected branch
  const fetchUnassignedMaterials = async (branchId) => {
    try {
      setMaterialsLoading(true)
      console.log('📦 Fetching unassigned materials for branch:', branchId)

      const response = await RawMaterialInventoryService.getUnassignedRawMaterials(branchId)

      if (response && response.success) {
        const materialsData = response.data?.results?.unassignedRawMaterials || []
        console.log('📦 Unassigned materials fetched:', materialsData)
        setUnassignedMaterials(Array.isArray(materialsData) ? materialsData : [])
      } else {
        console.warn('⚠️ Failed to fetch unassigned materials:', response)
        setUnassignedMaterials([])
      }
    } catch (error) {
      console.error('❌ Error fetching unassigned materials:', error)
      setUnassignedMaterials([])
    } finally {
      setMaterialsLoading(false)
    }
  }

  // Handle material selection
  const handleMaterialToggle = (materialId) => {
    setFormData(prev => {
      const isSelected = prev.selectedMaterials.includes(materialId)
      const newSelected = isSelected
        ? prev.selectedMaterials.filter(id => id !== materialId)
        : [...prev.selectedMaterials, materialId]

      return {
        ...prev,
        selectedMaterials: newSelected
      }
    })

    // Clear error when user selects materials
    if (errors.selectedMaterials) {
      setErrors(prev => ({
        ...prev,
        selectedMaterials: ''
      }))
    }
  }

  // Handle select all toggle
  const handleSelectAll = () => {
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)

    if (newSelectAll) {
      // Select all materials
      setFormData(prev => ({
        ...prev,
        selectedMaterials: unassignedMaterials.map(material => material._id)
      }))
    } else {
      // Deselect all materials
      setFormData(prev => ({
        ...prev,
        selectedMaterials: []
      }))
    }
  }

  // Update selectAll state when individual materials are selected
  useEffect(() => {
    if (unassignedMaterials.length > 0) {
      const allSelected = unassignedMaterials.every(material =>
        formData.selectedMaterials.includes(material._id)
      )
      setSelectAll(allSelected)
    }
  }, [formData.selectedMaterials, unassignedMaterials])

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.branch) {
      newErrors.branch = 'Branch is required'
    }

    if (formData.selectedMaterials.length === 0) {
      newErrors.selectedMaterials = 'Please select at least one raw material'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors)
      return
    }

    try {
      setLoading(true)

      // Prepare data for bulk assignment API
      const assignmentData = {
        branch: formData.branch,
        rawMaterials: formData.selectedMaterials.map(materialId => ({
          rawMaterial: materialId
        }))
      }

      console.log('📤 Assigning materials to inventory:', assignmentData)

      const response = await RawMaterialInventoryService.createMultipleInventories(assignmentData)

      if (response.success) {
        console.log('✅ Materials assigned successfully:', response)

        // Reset form
        setFormData({
          branch: '',
          selectedMaterials: []
        })
        setUnassignedMaterials([])
        setErrors({})
        setSelectAll(false)

        // Notify parent component
        const assignedInventories = response.data?.inventories || response.data || []
        onInventoryAssigned(assignedInventories)

        // Close modal
        onClose()
      } else {
        console.log('⚠️ Assignment failed with response:', response)
        setErrors({ general: response.message || 'Failed to assign materials to inventory' })
      }
    } catch (error) {
      console.error('❌ Error assigning materials:', error)

      let errorMessage = 'Failed to assign materials. Please try again.'

      if (error.message.includes('already assigned')) {
        errorMessage = 'Some materials are already assigned to this branch.'
      } else if (error.message.includes('validation')) {
        errorMessage = 'Please check your selection and try again.'
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      }

      setErrors({
        general: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  // Don't render if not open
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 ">
          <h2 className="text-xl font-semibold text-gray-900">Assign Raw Materials to Inventory</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {errors.general}
            </div>
          )}

          {/* Branch Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Branch name *
            </label>
            <div className="relative">
              <select
                value={formData.branch}
                onChange={handleBranchChange}
                disabled={loading}
                className={`w-full p-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 disabled:opacity-50 disabled:cursor-not-allowed appearance-none ${errors.branch ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-200'
                  }`}
                required
              >
                <option value="">Select branch</option>
                {branches.map(branch => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
            {errors.branch && (
              <p className="text-red-600 text-sm mt-1">{errors.branch}</p>
            )}
          </div>

          {/* Materials Selection */}
          {formData.branch && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Select Products
                </label>
                {unassignedMaterials.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    {selectAll ? 'Deselect all' : 'Select all'}
                  </button>
                )}
              </div>

              {materialsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">Loading materials...</span>
                </div>
              ) : unassignedMaterials.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600 text-sm">
                    No unassigned materials found for this branch
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    All raw materials are already assigned to this branch
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {unassignedMaterials.map(material => (
                    <label
                      key={material._id}
                      className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedMaterials.includes(material._id)}
                        onChange={() => handleMaterialToggle(material._id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        disabled={loading}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {material.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {material.code}
                          {material.unit && (
                            <span className="ml-2 bg-blue-100 text-blue-800 px-1 py-0.5 rounded">
                              {material.unit.abbreviation}
                            </span>
                          )}
                        </div>
                      </div>
                      {formData.selectedMaterials.includes(material._id) && (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </label>
                  ))}
                </div>
              )}

              {errors.selectedMaterials && (
                <p className="text-red-600 text-sm mt-1">{errors.selectedMaterials}</p>
              )}

              {formData.selectedMaterials.length > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {formData.selectedMaterials.length} material(s) selected
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.branch || formData.selectedMaterials.length === 0}
              className="flex-1 bg-black hover:bg-gray-800 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                'Add to Inventory'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssignInventoryModal